// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// German counterpart of onboarding.en.ts — see that file for provenance
// notes. Written in the same informal "du" voice as the rest of the app.
export const onboardingDe = {
    shell: {
        tuneLaterHint: "Geschmack, Budget und Vorräte? Das lernt Amy nebenbei — alles bleibt in den Einstellungen änderbar.",
        stepOf: "Schritt {{current}} von {{total}}",
        back: "Zurück",
        continue: "Weiter",
        finish: "Meinen ersten Plan erstellen",
        saving: "Speichert…",
        missingRequired: "Fast geschafft — ein Pflichtfeld (mit * markiert) ist noch nicht ausgefüllt.",
        missingRequiredField: "Fast geschafft — {{field}} ist noch nicht ausgefüllt.",
        saveErrorTitle: "Antwort konnte nicht gespeichert werden",
        saveErrorBody: "Prüfe deine Verbindung und versuche es erneut. Der Rest bleibt erhalten.",
        resumeNotice: "Weiter geht's da, wo du aufgehört hast.",
        languageSwitchNotice: "Deine Antworten bleiben beim Sprachwechsel erhalten.",
        knowsTitle: "Was BiteBalance bisher weiß",
        hardLimit: "Hart",
        knownGoal: "Ziel",
        knownTarget: "Tagesziel",
        knownActivity: "Alltagsbewegung",
        knownDiet: "Ernährung",
        knownAllergens: "Allergene",
        knownIntolerances: "Unverträglichkeiten",
        knownHealth: "Gesundheit",
        knownWeeknights: "Werktags",
        knownSnacks: "Snacks",
        knownNone: "Keine",
    },
    finishing: {
        title: "Dein Plan wird erstellt …",
        body: "Amy stellt gerade deine erste Woche mit Mahlzeiten zusammen. Das dauert meist weniger als eine Minute.",
        failedTitle: "Dein Plan konnte gerade nicht erstellt werden",
        failedBody: "Deine Angaben sind gespeichert. Geh weiter in die App — du kannst den Plan jederzeit von deiner Heute-Seite neu starten, nichts geht verloren.",
        timedOutTitle: "Noch in Arbeit",
        timedOutBody: "Die Erstellung deines Plans dauert gerade länger als gewöhnlich. Du kannst hier weiter warten oder jetzt fortfahren und in Kürze bei „Heute“ nachsehen — dein Plan erscheint dort, sobald er fertig ist.",
        continueAnyway: "Trotzdem fortfahren",
        errorDetail: "Technisches Detail: {{detail}}",
        errorTitle: "Dein Plan konnte nicht gestartet werden",
        errorBody: "Beim Starten deines ersten Plans ist etwas schiefgelaufen. Deine Antworten sind gespeichert — du kannst es gefahrlos erneut versuchen oder jetzt fortfahren und später einen Plan erstellen.",
        eyebrow: "Deine Woche entsteht",
        stageSaving: "Antworten speichern",
        stagePlanning: "Deine erste Woche zusammenstellen",
        stageReady: "Plan fertig",
        stageDone: "fertig",
        stageNow: "läuft",
        stageTodo: "ausstehend",
        stageFailed: "nicht abgeschlossen",
    },
    ineligible: {
        title: "Noch nicht für dich verfügbar",
        ageBody: "BiteBalance ist derzeit für Erwachsene (18+) in Deutschland verfügbar.",
        edBody: "Damit BiteBalance sicher bleibt, können wir Personen mit einer Vorgeschichte einer Essstörung noch keine Essensplanung anbieten. Sprich bitte mit einer Ärztin, einem Arzt oder einer vertrauten Fachperson über deine Ernährung — uns ist ehrliches Feedback wichtiger als ein unsicherer Plan.",
        contactCta: "Kontaktiere uns für spätere Neuigkeiten",
        backToStart: "Zurück zum Start",
    },
    correction: {
        belowFloorTitle: "Dieses Ziel liegt unter dem sicheren Minimum",
        belowFloorBody: "Ein Ziel von {{target}} kcal/Tag liegt unter dem sicheren Minimum von {{floor}} kcal/Tag für dein Profil. Wähle stattdessen das sichere Minimum oder passe deine Angaben an.",
        pregnancyGoalTitle: "Lass uns dein Ziel prüfen",
        pregnancyGoalBody: "Abnehmziele bieten wir während einer Schwangerschaft nicht an. Ändere dein Ziel auf „Gewicht halten“, bevor du fortfährst — BiteBalance plant nie ein unsicheres Ziel.",
        updateGoalCta: "Ziel aktualisieren",
        acknowledgeFloorLabel: "Mir ist klar, dass dies ein berechnetes Minimum ist, und ich möchte es verwenden.",
        useFloorCta: "Sicheres Minimum verwenden",
        breastfeedingLossTitle: "Ein sanfteres Abnehmziel während der Stillzeit",
        breastfeedingLossBody: "Wir können dir während der Stillzeit ein Abnehmziel anbieten — und gehen dabei sorgfältig vor:",
        breastfeedingLossPoint1Title: "Still-Kalorien werden hinzugerechnet",
        breastfeedingLossPoint1Body: "Dein Ziel berücksichtigt zuerst die zusätzlichen Kalorien, die das Stillen verbraucht — dein Körper kommt nie zu kurz.",
        breastfeedingLossPoint2Title: "Sanft aus Prinzip",
        breastfeedingLossPoint2Body: "Das Defizit ist auf etwa 0,5 kg/Woche oder weniger begrenzt und fällt nie unter ein sicheres Minimum für die Stillzeit — das schützt deine Milchbildung.",
        breastfeedingLossPoint3Title: "Das letzte Wort hat dein Behandlungsteam",
        breastfeedingLossPoint3Body: "Jede Situation ist anders — bitte sprich dein Ziel mit deiner Ärztin/deinem Arzt, deiner Hebamme oder einer auf Stillzeit spezialisierten Ernährungsberatung ab.",
        acknowledgeBreastfeedingLossLabel: "Mir ist klar, dass dies eine begrenzte, milchbildungsschonende Schätzung und keine medizinische Beratung ist, und ich werde sie mit meiner Ärztin/meinem Arzt, meiner Hebamme oder einer auf Stillzeit spezialisierten Ernährungsberatung abstimmen.",
        breastfeedingLossContinueCta: "Mit diesem Ziel fortfahren",
    },
    step1: {
        eyebrow: "SPRACHE · ERFORDERLICH",
        title: "Sprache wählen",
        body: "Die gesamte Einrichtung und App verwenden deine Auswahl.",
        de: "Deutsch",
        deSub: "Deutschland",
        en: "English",
        enSub: "Germany",
    },
    step2: {
        namesOptionalHint: "Optional — damit Amy dich mit Namen begrüßen kann. Jederzeit nachtragbar.",
        eyebrow: "VORAUSSETZUNGEN · ERFORDERLICH",
        title: "Wo startest du?",
        body: "BiteBalance ist derzeit für Erwachsene in Deutschland verfügbar.",
        eligibleOption: "Ich bin mindestens 18 Jahre alt und lebe in Deutschland",
        eligibleSub: "Einrichtung fortsetzen",
        ineligibleOption: "Ich erfülle die Voraussetzungen noch nicht",
        ineligibleSub: "Kontaktiere uns für spätere Neuigkeiten",
        firstNameLabel: "Vorname",
        familyNameLabel: "Nachname",
        dobLabel: "Geburtsdatum",
        dobHint: "BiteBalance ist für Erwachsene (18+). Dein Geburtsdatum bleibt privat.",
        dobUnderage: "BiteBalance ist ab 18 Jahren verfügbar. Dieses Geburtsdatum passt nicht zur 18+-Bestätigung.",
        dobInvalid: "Gib ein gültiges Geburtsdatum ein.",
    },
    step3: {
        eyebrow: "ZIEL · ERFORDERLICH",
        title: "Wobei soll dich deine Ernährung unterstützen?",
        body: "Wähle dein Hauptziel. Du kannst es später ändern.",
        maintain: "Gewicht halten",
        maintainSub: "Gewicht stabil halten",
        loss: "Langsam abnehmen",
        lossSub: "Schrittweise Fortschritte machen",
        gain: "Zunehmen",
        gainSub: "Gesunde Gewichtszunahme unterstützen",
        improve: "Meine Gesundheit verbessern",
        improveSub: "Rundum besser fühlen — ausgewogene Ziele, ohne Gewichtsrichtung",
        ackLabel: "Mir ist klar, dass BiteBalance mir als Nächstes ein Ziel zur Prüfung vorschlägt.",
    },
    step4: {
        eyebrow: "DEIN AUSGANGSPUNKT · ERFORDERLICH",
        title: "Erzähl uns etwas über dich",
        body: "So kann BiteBalance dir vor der Planung ein tägliches Ernährungsziel zur Prüfung vorschlagen.",
        sexLabel: "Geschlecht (für die Berechnung)",
        sexFemale: "Weiblich",
        sexMale: "Männlich",
        preferNotToAnswer: "Keine Angabe",
        ageLabel: "Alter",
        heightLabel: "Größe (cm)",
        weightLabel: "Gewicht (kg)",
        targetWeightLabel: "Zielgewicht (kg, optional)",
        activityLevelLabel: "Tägliche Aktivität",
        activityLevelBody: "Wie viel du dich im Alltag bewegst — nicht dein Training, danach fragen wir gleich.",
        activitySedentary: "Meist sitzend",
        activitySedentaryBody: "Schreibtischjob, meist sitzend, wenig Bewegung tagsüber",
        activityLight: "Leicht aktiv",
        activityLightBody: "Etwas Gehen oder leichte Hausarbeit, aber meist drinnen",
        activityModerate: "Oft auf den Beinen",
        activityModerateBody: "Einzelhandel, Unterricht, Kinderbetreuung oder ein Job mit viel Bewegung",
        activityHigh: "Körperlich anstrengend",
        activityHighBody: "Handwerk, Lieferdienst, Gastronomie — den Großteil des Tages auf den Beinen",
        sportsQuestion: "Treibst du Sport oder machst du strukturiertes Training?",
        sportsYes: "Ja",
        sportsNo: "Aktuell nicht",
        sportsAddCta: "Sportart hinzufügen",
        sportsRemove: "Entfernen",
        sportsTypes: {
            strength: "Krafttraining",
            running: "Laufen",
            cycling: "Radfahren",
            swimming: "Schwimmen",
            teamSports: "Mannschaftssport",
            yogaPilates: "Yoga / Pilates",
            walkingHiking: "Gehen / Wandern",
            other: "Andere",
        },
        sportsSessionsLabel: "Einheiten pro Woche",
        sportsIntensityLabel: "Intensität",
        sportsIntensity: {
            low: "Niedrig",
            moderate: "Mittel",
            high: "Hoch",
        },
        sportsMinutesLabel: "Minuten pro Einheit (optional)",
        sportsMinutesPlaceholder: "z. B. 45",
        wearableQuestion: "Zeigt deine Uhr oder dein Fitness-Tracker aktiv verbrannte Kalorien an?",
        wearableLabel: "Durchschnittliche aktive Kalorien pro Tag (optional)",
        wearableHint: "Aus dem Wert „Aktivkalorien“ oder „verbrannte Kalorien“ deines Geräts, gemittelt über ein bis zwei Wochen. Wenn du das einträgst, verwenden wir diesen Wert direkt anstelle der Aktivitätsstufe oben.",
        methodLabel: "Zielberechnung",
        methodCalculated: "Mein Ziel für mich berechnen",
        methodManual: "Ich möchte mein Ziel selbst eingeben",
        manualLabel: "Tägliches Energieziel (kcal)",
        reviewTitle: "Dein vorgeschlagenes Ziel",
        reviewBody: "Basierend auf deinen Angaben schlägt BiteBalance {{kcal}} kcal/Tag für dein Ziel vor.",
        floorNote: "BiteBalance schlägt oder akzeptiert nie ein Ziel unter dem sicheren Minimum.",
        sourcesLink: "Quellen & Methode",
        recommendedBadge: "Empfohlen",
        calculatedIntro: "Wir berechnen dir ein Ziel auf Basis deiner Angaben oben.",
        useCalculatedCta: "Stattdessen berechnetes Ziel verwenden",
        expectationTitle: "Das ist eine Schätzung, keine Garantie",
        expectationBody: "Dein Ernährungsziel ist eine Schätzung auf Basis der Angaben, die du machst. Ergebnisse unterscheiden sich von Person zu Person, und das Gewicht kann von Tag zu Tag schwanken. BiteBalance hilft dir beim Planen, Nachverfolgen und Anpassen, garantiert aber keine bestimmte Veränderung oder ein bestimmtes Datum, an dem ein Ziel erreicht ist.",
    },
    step5: {
        eyebrow: "ERNÄHRUNGSWEISE · ERFORDERLICH",
        title: "Wie ernährst du dich?",
        body: "Diese Auswahl gilt für jeden Plan.",
        omnivore: "Omnivor",
        pescatarian: "Pescetarisch",
        vegetarian: "Vegetarisch",
        vegan: "Vegan",
    },
    step6: {
        eyebrow: "ALLERGIEN · ERFORDERLICH",
        title: "Allergien oder Unverträglichkeiten?",
        body: "Wähle alles Zutreffende oder „Keine“.",
        allergens: {
            peanut: "Erdnüsse",
            treeNut: "Schalenfrüchte",
            milk: "Milch (Allergie)",
            gluten: "Gluten (Getreide)",
            egg: "Eier",
            fish: "Fisch",
            shellfish: "Krebstiere (Garnelen, Krabben)",
            soy: "Soja",
            sesame: "Sesam",
            celery: "Sellerie",
            mustard: "Senf",
            sulphites: "Schwefeldioxid/Sulfite",
            lupin: "Lupinen",
            mollusc: "Weichtiere (Muscheln, Tintenfisch)",
        },
        intolerancesLabel: "Weitere Unverträglichkeiten (optional)",
        intolerancesPlaceholder: "z. B. Histamin, FODMAPs",
        otherLabel: "Andere Allergie oder Unverträglichkeit",
        otherPlaceholder: "Lebensmittel oder Zutat eingeben",
        noneLabel: "Keine",
        hardNoteTitle: "Deine Sicherheit hat Priorität",
        hardNoteBody: "Diese Angaben werden dauerhaft aus jedem für dich erstellten Plan ausgeschlossen und können später nur mit einer Bestätigung geändert werden. Kreuzkontakt ('kann Spuren enthalten') wird ausgeschlossen, wo Produkte ihn deklarieren. Quellendaten zu Allergenen können unvollständig sein — prüfe immer die Produktkennzeichnung und bestätige selbst, dass jede Mahlzeit für dich sicher ist, bevor du sie kochst oder isst. Weil Allergieangaben Gesundheitsdaten sind, verarbeiten wir sie nur mit deiner Einwilligung — die du durch das Ausfüllen dieses Schritts gibst — und du kannst sie jederzeit unter „Essen & Gesundheit“ einsehen, ändern oder löschen.",
        hardNoteWhy: "Warum wir das fragen: Damit Amy unsichere Mahlzeiten von vornherein ausschließen kann — nicht erst im Nachhinein markiert.",
        nutClarifierPeanuts: "Du hast Schalenfrüchte gewählt. Erdnüsse sind botanisch anders (Hülsenfrucht) — viele Nussallergien betreffen beide. Erdnüsse auch ausschließen?",
        nutClarifierTreeNuts: "Du hast Erdnüsse gewählt. Schalenfrüchte (Mandeln, Walnüsse …) sind eine eigene Allergenklasse. Auch ausschließen?",
        nutClarifierAdd: "Ja, beide ausschließen",
        nutClarifierKeep: "Nein, Auswahl behalten",
        structuredIntoleranceRequired: "Für Laktose nutze die Option \"Laktoseintoleranz\" und wähle, wie streng sie ist.",
        lactoseChip: "Laktoseintoleranz",
        lactoseLevelTitle: "Wie streng ist deine Laktoseintoleranz?",
        lactoseLevelAvoidAll: "Ich meide alle Milchprodukte",
        lactoseLevelAvoidAllSub: "Milch, Käse, Joghurt, Butter und Sahne bleiben aus jedem Plan draußen.",
        lactoseLevelFreeOk: "Laktosefreie Milchprodukte sind okay",
        lactoseLevelFreeOkSub: "Als laktosefrei gekennzeichnete Produkte bleiben drin. Normale Milch, Sahne, Quark und die meisten Frischkäse bleiben draußen — genauso wie Milchprodukte ohne geprüfte Laktoseangabe.",
        lactoseLevelNote: "Deine Entscheidung, keine Diagnose — jederzeit unter Essen & Gesundheit änderbar.",
        lactoseTextHint: "Nutze oben die Option \"Laktoseintoleranz\" — sie hat ihre eigene Einstellung.",
    },
    step13: {
        eyebrow: "GESUNDHEIT · ERFORDERLICH",
        title: "Gibt es gesundheitliche Aspekte, die wir kennen sollten?",
        body: "Das schaltet keine therapeutische oder medizinische Diät frei — es hilft BiteBalance nur, die richtigen Lebensmittelsicherheitsregeln und Hinweise für dich anzuwenden. Wähle alles Zutreffende oder „Keine“.",
        conditions: {
            pregnancy: "Schwangerschaft",
            breastfeeding: "Stillzeit",
            type2_diabetes: "Typ-2-Diabetes",
            hypertension: "Bluthochdruck",
            kidney_disease: "Chronische Nierenerkrankung",
            celiac: "Zöliakie",
            eating_disorder_history: "Vorgeschichte einer Essstörung",
            other: "Andere Erkrankung",
        },
        otherLabel: "Beschreibe die andere Erkrankung",
        otherPlaceholder: "Erkrankung eingeben",
        medicationsLabel: "Aktuelle Medikamente (optional, Freitext)",
        medicationsPlaceholder: "z. B. Metformin, Levothyroxin",
        noneLabel: "Keine",
        disclaimerTitle: "Bitte beachte:",
        disclaimerBody: "BiteBalance wendet evidenzbasierte Lebensmittelsicherheitsregeln für die ausgewählten Erkrankungen an, erstellt aber keinen medizinischen oder therapeutischen Ernährungsplan. Du kannst deine Angaben jederzeit unter „Essen & Gesundheit“ einsehen, ändern oder löschen.",
        consultDoctor: "Sprich für deine individuellen Bedürfnisse immer mit einer Ärztin, einem Arzt oder einer Ernährungsfachkraft.",
    },
    step7: {
        eyebrow: "GESCHMACK · ERFORDERLICH",
        title: "Was isst du gerne?",
        body: "Sag Amy, was du liebst und was nie in deinem Plan stehen soll. Wähle „Keine“, wenn nichts zutrifft.",
        likesLabel: "Lebensmittel oder Küchen, die du magst",
        likesPlaceholder: "z. B. Mediterran, Linsen, knackiges Gemüse",
        dislikesLabel: "Lebensmittel oder Kombinationen, die du nicht magst",
        dislikesPlaceholder: "z. B. weiche Pilze",
        neverEatLabel: "Füge Lebensmittel hinzu, die nicht in deinen Mahlzeiten vorkommen sollen.",
        neverEatPlaceholder: "z. B. Schweinefleisch, Koriander",
        dislikedCombinationsLabel: "Kombinationen, die du nicht magst",
        dislikedCombinationsPlaceholder: "z. B. nie Ananas mit herzhaften Gerichten",
        noneButton: "Nichts davon trifft zu",
    },
    step8: {
        eyebrow: "KOCHEN · ERFORDERLICH",
        title: "Wie gerne kochst du?",
        body: "Deine Erfahrung und Freude am Kochen bestimmen Aufwand, Vielfalt und Anleitungen.",
        beginner: "Anfänger",
        beginnerSub: "Einfache Techniken",
        intermediate: "Fortgeschritten",
        intermediateSub: "Sicher beim Kochen",
        advanced: "Sehr erfahren",
        advancedSub: "Mehr Techniken",
        enjoymentHigh: "Ich koche gerne",
        enjoymentLow: "So wenig wie möglich",
    },
    step9: {
        eyebrow: "ZEIT + KÜCHE · ERFORDERLICH",
        title: "Was passt an einem normalen Wochentag?",
        body: "Amy kann das Zeitlimit später je nach Mahlzeit oder Tag anpassen.",
        perMealLabel: "Minuten pro Mahlzeit",
        perDayLabel: "Minuten pro Tag",
        presetUpTo: "bis {{minutes}} Min.",
        equipmentLabel: "Ausstattung oder Einschränkungen",
        equipmentPlaceholder: "z. B. eine Pfanne, Ofen, Mixer — oder keine",
    },
    step10: {
        eyebrow: "KOCHSTIL · ERFORDERLICH",
        title: "Wie möchtest du diese Woche kochen?",
        body: "Koche täglich, bereite mehrere Portionen auf einmal zu oder kombiniere beides.",
        daily: "Täglich kochen",
        batch: "Vorkochen",
        hybrid: "Beides kombinieren",
        choose: "Auswählen",
        snackLabel: "Möchtest du Snacks in deinem Plan?",
        snackNone: "Keine Snacks",
        snackMorning: "Vormittagssnack",
        snackAfternoon: "Nachmittagssnack",
        snackBoth: "Beides",
    },
    step11: {
        eyebrow: "BUDGET + GESCHÄFT · ERFORDERLICH",
        title: "Was passt zu deinem Wocheneinkauf?",
        body: "Wähle ein Budget, dein übliches Geschäft und deine bevorzugte Einkaufsart.",
        under50: "Unter 50 €/Woche",
        between: "50–80 €/Woche",
        noTarget: "Kein festes Budget",
        retailerLabel: "Bevorzugte(s) Geschäft(e)",
        noPreference: "Keine Präferenz",
        deliveryLabel: "Einkaufsart",
        pickup: "Abholung",
        delivery: "Lieferung",
        either: "Egal",
        none: "Keine Präferenz",
        offerEmailsTitle: "💌 Verpasse keine Angebote deiner Märkte",
        offerEmailsBody: "Wenn deine bevorzugten Märkte Angebote haben, die zu deinem Plan oder deiner Einkaufsliste passen, geben wir dir Bescheid — und Amy kann die Ersparnis direkt in deine Woche einbauen.",
        offerEmailsToggle: "Ja, schickt mir passende Angebote per E-Mail",
        offerEmailsFinePrint: "Höchstens 1 E-Mail pro Woche · nur mit deiner Zustimmung · jederzeit mit einem Klick abbestellbar",
        offerEmailsSaved: "Gespeichert — wir behalten deine Märkte für dich im Blick.",
    },
    step12: {
        eyebrow: "VORHANDENE LEBENSMITTEL · OPTIONAL",
        title: "Nutze, was bereits in deiner Küche ist",
        body: "Optional — du kannst diesen Schritt einfach überspringen. Oder schreib eine kurze Notiz in deinen eigenen Worten und Amy zeigt dir die erkannten Lebensmittel und Mengen zur Bestätigung.",
        pantryLabel: "Lebensmittel, die du bereits hast",
        pantryPlaceholder: "Ich habe sechs Eier, einen halben Beutel Spinat und etwa 300 g Reis.",
        nothingToAdd: "Jetzt nichts hinzufügen",
        extractButton: "Meine Lebensmittel prüfen",
        candidatesTitle: "Lebensmittel prüfen",
        candidatesBody: "Bestätige die Mengen, bevor wir sie zu deiner Küche hinzufügen.",
        confirmButton: "Lebensmittel bestätigen",
        reviewTitle: "Deine Antworten prüfen",
        reviewHardTitle: "Feste Regeln (werden nie überschritten)",
        reviewSoftTitle: "Weiche Vorlieben (jederzeit anpassbar)",
        reviewHardBody: "Allergene, Unverträglichkeiten, Ernährungsweise und Lebensmittelsicherheits-Ausschlüsse aus deinen Gesundheitsangaben.",
        reviewSoftBody: "Geschmack, Kochstil, Zeit, Budget und alles andere, das Amy anpassen kann.",
        notMedicalTitle: "Keine medizinische Beratung",
        notMedicalBody: "Amy ist der KI-Assistent von BiteBalance und ersetzt keine Ärztin, keinen Arzt und keine Ernährungsfachkraft.",
        consentLabel: "Ich habe meine Antworten geprüft und möchte, dass BiteBalance meinen ersten Plan erstellt.",
        createPlanButton: "Meinen ersten Plan erstellen",
    },
};
export const moreDe = {
    hub: {
        sectionYou: "Über dich",
        sectionFood: "Essen & Geschmack",
        sectionApp: "App & Konto",
        profileLink: "Persönliche Angaben",
        profileLinkSub: "Dein Name, Foto, Geburtsdatum und Kontaktdaten",
        preferencesLink: "Einstellungen",
        preferencesLinkSub: "Kochen, Planungsmodus, Snacks, Aktivität, Budget und Benachrichtigungen",
        goalsLink: "Ziele",
        goalsLinkSub: "Dein Ziel, Kalorienziel, Körperdaten und Zeitbudget",
        constraintsLink: "Essen & Gesundheit",
        constraintsLinkSub: "Allergien, gemiedene Lebensmittel und Gesundheitsangaben — werden in deinen Plänen immer berücksichtigt",
        memoryLink: "Erinnerungen",
        memoryLinkSub: "Was Amy sich über deinen Geschmack merkt, und offene Vorschläge",
        guidesLink: "Guide-Center",
        guidesLinkSub: "Kurze ehrliche Guides: ungeplante Mahlzeiten, die Waage und wie Amy plant",
        sourcesLink: "Quellen & Methode",
        sourcesLinkSub: "Die veröffentlichten Quellen hinter deinem Kalorienziel, den Makros und den Sicherheitsminima",
    },
    profile: {
        title: "Persönliche Angaben",
        back: "Zurück zu Einstellungen",
        personalTitle: "Persönliche Daten",
        preferencesTitle: "Vorlieben",
        firstNameLabel: "Vorname",
        familyNameLabel: "Nachname",
        dobLabel: "Geburtsdatum",
        emailLabel: "E-Mail-Adresse",
        emailReadOnlyHint: "Deine Anmelde-E-Mail kann hier nicht geändert werden.",
        phoneLabel: "Mobilnummer (optional)",
        phoneOptionalHint: "z. B. +49 151 2345678",
        phoneInvalid: "Gib eine gültige Telefonnummer ein (6-15 Ziffern, optional +).",
        photoAlt: "Profilfoto",
        photoUpload: "Foto hinzufügen",
        photoReplace: "Foto ändern",
        photoRemove: "Foto entfernen",
        photoUploading: "Wird hochgeladen…",
        photoError: "Das Foto konnte nicht hochgeladen werden. Bitte versuche es erneut.",
        completeBannerTitle: "Vervollständige dein Profil",
        completeBannerBody: "Ergänze deinen Namen und dein Geburtsdatum, um dein Profil zu vervollständigen. Alles funktioniert auch so weiter.",
        personalSaved: "Persönliche Daten gespeichert.",
        personalSaveError: "Deine persönlichen Daten konnten nicht gespeichert werden. Bitte versuche es erneut.",
        goalLabel: "Ziel",
        calorieTargetLabel: "Tägliches Kalorienziel",
        cookingLabel: "Freude am Kochen & Erfahrung",
        planningModeLabel: "Kochstil",
        snackLabel: "Snacks in deinem Plan",
        budgetLabel: "Wochenbudget",
        retailersLabel: "Bevorzugte Geschäfte",
        saveButton: "Änderungen speichern",
        saved: "Gespeichert.",
        saveError: "Das konnte nicht gespeichert werden. Bitte versuche es erneut.",
        snackRegenTitle: "Snack-Einstellung gespeichert",
        snackRegenBody: "Dein aktueller Plan behält seine Mahlzeiten. Die neue Snack-Einstellung gilt ab der nächsten Planerstellung.",
        snackRegenCta: "Plan jetzt neu erstellen",
        snackRegenStarted: "Die Planerstellung wurde gestartet. Dein neuer Plan erscheint in Kürze.",
        snackRegenError: "Die Neuerstellung konnte nicht gestartet werden. Bitte versuche es erneut.",
        // ADR-029: ehrliche Einwilligungs-Beschreibung — standardmäßig aus,
        // höchstens eine E-Mail pro Woche, jederzeit widerrufbar.
        notificationsTitle: "Benachrichtigungen",
        offerEmailsLabel: "Angebots-E-Mails",
        offerEmailsBody: "Höchstens eine E-Mail pro Woche, wenn aktuelle Angebote bei deinen bevorzugten Geschäften zu deinem Plan oder deiner Einkaufsliste passen. Standardmäßig aus — du kannst sie hier jederzeit wieder deaktivieren, oder über den Abmeldelink in jeder E-Mail.",
        offerEmailsSaved: "Benachrichtigungseinstellung gespeichert.",
        offerEmailsError: "Die Einstellung konnte nicht gespeichert werden. Bitte versuche es erneut.",
        requiredFieldError: "Dieses Feld ist erforderlich und darf nicht leer bleiben.",
        bodyTitle: "Körperdaten",
        bodyIntro: "Geschlecht, Alter, Größe und Gewicht bestimmen dein tägliches Kalorienziel. Änderungen berechnen das Ziel neu und aktualisieren deinen Plan.",
        bodySaved: "Gespeichert.",
        bodySaveError: "Deine Körperdaten konnten nicht gespeichert werden. Bitte versuche es erneut.",
        bodyInvalid: "Bitte prüfe die markierten Werte.",
        bodyTargetKept: "Dein Tagesziel liegt jetzt bei {{to}} (vorher {{from}}). Deine aktuelle Woche und deine Einkaufsliste bleiben genau wie geplant — das neue Ziel gilt ab deinem nächsten Plan.",
        bodyTargetUnchanged: "Gespeichert. Dein Kalorienziel bleibt unverändert.",
        bodyRebuildOffer: "Das ist eine größere Änderung. Wenn du die Woche lieber darauf ausrichten möchtest, kannst du sie neu aufbauen — das ersetzt die verbleibenden Mahlzeiten und ändert deine Einkaufsliste.",
        bodyRebuildCta: "Woche neu aufbauen",
        dietaryStyleLabel: "Ernährungsstil",
        dietaryStyleHint: "Eine harte Regel — deine Pläne überschreiten sie nie. Eine Änderung baut deinen Plan neu.",
        timeBudgetLabel: "Kochzeit",
        timeBudgetHint: "Wie lange eine normale Mahlzeit unter der Woche dauern darf. Eine Änderung baut deinen Plan neu.",
    },
    goals: {
        ownTargetCta: "Eigenes Ziel festlegen",
        ownTargetLabel: "Dein Tagesziel (kcal)",
        ownTargetBody: "Deine Entscheidung, ehrlich umgesetzt: Pläne werden auf diese Zahl gebaut statt auf die berechnete. Du kannst jederzeit zurückwechseln.",
        ownTargetSave: "Dieses Ziel verwenden",
        ownTargetCancel: "Abbrechen",
        ownTargetRestore: "Zurück zum berechneten Ziel",
        floorDisclaimerTitle: "Das liegt unter deiner Sicherheitsuntergrenze",
        floorDisclaimerBody: "Die Untergrenze schützt dich — in der Stillzeit schützt sie auch deine Milchbildung. Wenn du fortfährst, bestätigst du, dass du dieses Ziel eigenverantwortlich wählst und es mit Ärztin, Hebamme oder Ernährungsberatung besprichst.",
        floorDisclaimerAck: "Ich verstehe das und übernehme die Verantwortung für dieses Ziel.",
        ownTargetError: "Das Ziel konnte nicht gespeichert werden. Bitte versuche es erneut.",
        title: "Ziele",
        back: "Zurück zu Einstellungen",
        goalLabel: "Dein Ziel",
        targetTitle: "Tägliches Kalorienziel",
        computedNote: "Berechnet mit der Mifflin-St-Jeor-Formel aus deinen Körperdaten und deinem Ziel — kein Versprechen, sondern ein Planungsziel.",
        manualNote: "Von dir manuell festgelegt. Pläne werden gegen dieses Ziel validiert.",
        toleranceLabel: "Pläne bleiben innerhalb von ±{{percent}}% dieses Ziels.",
        macrosTitle: "Tägliche Makros",
        macrosHint: "So verteilen sich deine täglichen Kalorien auf Eiweiß, Kohlenhydrate und Fett. Wir berechnen das automatisch aus deinem Kalorienziel und deinem Ziel — du musst nichts selbst einstellen.",
        proteinLabel: "Protein",
        carbsLabel: "Kohlenhydrate",
        fatLabel: "Fett",
        sources: {
            title: "Quellen & Methode",
            eyebrow: "So rechnet BiteBalance",
            linkLabel: "Quellen & Methode",
            intro: "Dein Kalorienziel ist ein Planungsziel, keine medizinische Verordnung. Hier stehen die veröffentlichten Quellen hinter jeder Zahl, die BiteBalance zu Energie und Nährstoffen nennt. Sprich mit deiner Ärztin, deinem Arzt oder einer Ernährungsfachkraft, bevor du deine Ernährung wegen einer Erkrankung, in der Schwangerschaft oder in der Stillzeit umstellst.",
            openHint: "Links öffnen sich in deinem Browser.",
            groups: {
                target: {
                    title: "Tägliches Kalorienziel",
                    body: "Der Grundumsatz wird mit der Mifflin-St-Jeor-Formel aus Geschlecht, Alter, Größe und Gewicht berechnet. Deine Alltagsbewegung und dein angegebener Sport kommen dazu (Sport über MET-Werte aus dem Compendium of Physical Activities oder, falls eingetragen, der Aktivkalorien-Durchschnitt deines Wearables). Das Ergebnis wird für dein Ziel angepasst: −15 % beim Abnehmen, +10 % beim Zunehmen, unverändert bei Halten und Gesundheit. Pläne bleiben innerhalb von ±10 % des Ziels.",
                },
                macros: {
                    title: "Eiweiß, Kohlenhydrate und Fett",
                    body: "Deine Kalorien werden zu 25 % Eiweiß, 45 % Kohlenhydraten und 30 % Fett aufgeteilt (Eiweiß und Kohlenhydrate mit 4 kcal/g, Fett mit 9 kcal/g). Diese Aufteilung liegt innerhalb der Acceptable Macronutrient Distribution Ranges des US Institute of Medicine und der DGE-Referenzwerte.",
                },
                floor: {
                    title: "Sicherheitsminima",
                    body: "BiteBalance schlägt oder akzeptiert nie ein Ziel unter deinem Grundumsatz oder unter 1.200 kcal/Tag (Frauen) bzw. 1.500 kcal/Tag (Männer). Zum Vergleich: Klinische Leitlinien setzen ärztlich begleitete kalorienreduzierte Diäten bei 1.000–1.200 kcal/Tag für die meisten Frauen und 1.200–1.600 kcal/Tag für Männer an; die Minima von BiteBalance liegen über dem unteren Ende dieses Bereichs und sind eine Schutzgrenze, keine Empfehlung.",
                },
                breastfeeding: {
                    title: "Stillzeit",
                    body: "Wenn du angegeben hast, dass du stillst, kommen bei jedem Ziel 330 kcal/Tag zu deinem Energiebedarf dazu, ein Abnehm-Defizit wird auf etwa 0,5 kg pro Woche begrenzt und das Minimum steigt auf 1.800 kcal/Tag. Veröffentlichte Empfehlungen beziffern den Mehrbedarf mit 330–400 kcal/Tag (CDC, US Institute of Medicine) bzw. +500 kcal/Tag bei ausschließlichem Stillen (DGE).",
                },
            },
            items: {
                mifflin: {
                    title: "Mifflin MD, St Jeor ST et al. (1990)",
                    detail: "A new predictive equation for resting energy expenditure in healthy individuals. American Journal of Clinical Nutrition 51(2), 241–247.",
                },
                compendium: {
                    title: "Ainsworth BE et al. (2011)",
                    detail: "2011 Compendium of Physical Activities: a second update of codes and MET values. Medicine & Science in Sports & Exercise 43(8), 1575–1581.",
                },
                iom: {
                    title: "US Institute of Medicine (2005)",
                    detail: "Dietary Reference Intakes for Energy, Carbohydrate, Fiber, Fat, Fatty Acids, Cholesterol, Protein, and Amino Acids — Makronährstoff-Bereiche und Energie in der Stillzeit.",
                },
                dge: {
                    title: "Deutsche Gesellschaft für Ernährung (DGE)",
                    detail: "Referenzwerte für die Nährstoffzufuhr: Energie — einschließlich des Richtwerts für Stillende.",
                },
                nhlbi: {
                    title: "NHLBI / NAASO (2000)",
                    detail: "The Practical Guide: Identification, Evaluation, and Treatment of Overweight and Obesity in Adults (PDF) — Bereiche kalorienreduzierter Diäten und das Tempo von 0,5–1 kg pro Woche.",
                },
                cdc: {
                    title: "US Centers for Disease Control and Prevention",
                    detail: "Maternal Diet and Breastfeeding — der Mehrbedarf von 330–400 kcal/Tag für gut ernährte stillende Mütter.",
                },
            },
        },
        bodyTitle: "Körperdaten",
        currentWeightLabel: "Aktuelles Gewicht",
        targetWeightLabel: "Zielgewicht",
        notProvided: "Nicht angegeben",
        bodyEditHint: "Deine Körperdaten stammen aus dem Onboarding. Änderungen berechnen deine Ziele neu.",
        timeBudgetTitle: "Zeitbudget",
        perMealLabel: "Pro Mahlzeit",
        perDayLabel: "Pro Tag",
        dietaryStyleTitle: "Ernährungsstil",
        dietaryStyleEditLink: "Ernährungsstil unter Einstellungen ändern",
        saveButton: "Ziel speichern",
        saved: "Gespeichert.",
        saveError: "Das konnte nicht gespeichert werden. Bitte versuche es erneut.",
        regenTitle: "Ziel gespeichert",
        regenBody: "Dein aktueller Plan behält seine Mahlzeiten. Das neue Ziel gilt ab der nächsten Planerstellung.",
        regenCta: "Plan jetzt neu erstellen",
        regenStarted: "Die Neuerstellung wurde gestartet. Dein neuer Plan erscheint in Kürze.",
        regenError: "Die Neuerstellung konnte nicht gestartet werden. Bitte versuche es erneut.",
    },
    constraints: {
        title: "Essen & Gesundheit",
        back: "Zurück zu Einstellungen",
        allergensTitle: "Was du nicht isst",
        allergySectionTitle: "Allergien & Unverträglichkeiten",
        avoidSectionTitle: "Lebensmittel, die ich nicht esse",
        noneChip: "Keine",
        healthTitle: "Gesundheitsangaben",
        editButton: "Bearbeiten",
        dialogTitle: "Änderung bestätigen",
        dialogBody: "Das Ändern einer festen Regel erstellt deinen aktuellen Plan neu, damit er sicher bleibt. Das kann nicht automatisch rückgängig gemacht werden.",
        dialogConfirmLabel: "Mir ist klar, dass mein Plan dadurch neu erstellt wird.",
        confirmButton: "Änderung bestätigen",
        cancelButton: "Abbrechen",
        updated: "Aktualisiert. Dein Plan wird entsprechend neu erstellt.",
        updateError: "Diese Änderung konnte nicht gespeichert werden. Bitte versuche es erneut.",
        hardPill: "Hart",
        hardNote: "Harte Grenzen werden nie umgangen, abgeschwächt oder verlernt.",
        addButton: "Hinzufügen",
        intoleranceSectionTitle: "Unverträglichkeiten",
        intoleranceLactose: "Laktoseintoleranz",
        levelAvoidAll: "meidet alle Milchprodukte",
        levelFreeOk: "laktosefreie Milchprodukte sind okay",
        intoleranceTextLabel: "Weitere Unverträglichkeiten",
        clarifyMilkTitle: "Eine Frage zu Milch",
        clarifyMilkBody: "Du hattest \"Milch/Laktose\" ausgewählt. Ist das eine Milchallergie oder eine Laktoseintoleranz? Bis du antwortest, behandeln wir es weiter als Milchallergie — die sicherere Lesart.",
        clarifyMilkAllergy: "Es ist eine Milchallergie",
        clarifyMilkIntolerance: "Es ist eine Laktoseintoleranz",
        clarifyLevelTitle: "Wie streng ist deine Laktoseintoleranz?",
        clarifyLevelBody: "Du hast uns von einer Laktoseintoleranz erzählt, bevor wir nach der Strenge gefragt haben. Bis du wählst, bleiben alle Milchprodukte draußen.",
        clarifyConfirm: "Antwort speichern",
        errorLevelRequired: "Wähle vor dem Speichern, wie streng deine Laktoseintoleranz ist.",
        errorLevelNotAllowed: "Eine Strenge-Stufe gilt nur für die Option Laktoseintoleranz.",
        errorClarificationUnresolved: "Beantworte die Milch-Frage eindeutig, bevor du speicherst.",
    },
    memory: {
        title: "Erinnerungen",
        back: "Zurück zu Einstellungen",
        loadError: "Deine Erinnerungen konnten nicht geladen werden. Bitte versuche es erneut.",
        tastesTitle: "Geschmäcker, die Amy kennt",
        tastesDislikes: "Vermeidet für dich",
        tastesLikes: "Du liebst",
        tastesEmpty: "Noch keine gespeicherten Vorlieben — sag es Amy im Chat (\"Ich mag kein Bacon\") und es erscheint hier.",
        tastesHint: "Gespeicherte Vorlieben steuern jeden künftigen Plan. Bearbeiten unter Präferenzen.",
        pendingTitle: "Wartet auf deine Bestätigung",
        pendingEmpty: "Nichts offen. Amy fragt dich, bevor sie sich etwas Neues merkt.",
        firstRunTitle: "Amy hat noch nichts gespeichert",
        firstRunBody: "Wenn du Amy Dinge erzählst, die es wert sind — Vorlieben, Gewohnheiten, Lebenskontext — erscheinen sie zuerst HIER zu deiner Freigabe. Nichts wird je hinter deinem Rücken gespeichert.",
        firstRunCta: "Mit Amy sprechen",
        confirmButton: "Merken",
        rejectButton: "Nein, nicht merken",
        confirmError: "Das konnte nicht gespeichert werden. Bitte versuche es erneut.",
        rejectError: "Das konnte nicht verworfen werden. Bitte versuche es erneut.",
        confirmedTitle: "Was Amy sich merkt",
        confirmedEmpty: "Noch nichts gemerkt. Bestätige einen Vorschlag von Amy, damit er hier erscheint.",
        originLabel: "Herkunft",
        originAmyChat: "Aus einem Gespräch mit Amy",
        effectLabel: "Auswirkung auf deine Pläne",
        effectIngredientDislike: "Diese Zutat wird in künftigen Wochenplänen seltener ausgewählt.",
        effectIngredientLike: "Diese Zutat wird in künftigen Wochenplänen bevorzugt.",
        effectRecipeDislike: "Dieses Rezept wird in künftigen Wochenplänen seltener ausgewählt.",
        effectRecipeLike: "Dieses Rezept wird in künftigen Wochenplänen bevorzugt.",
        effectCuisine: "Diese Küche wird in künftigen Wochenplänen bevorzugt.",
        effectNote: "Fließt nur in Amys Gespräche ein — dein Plan und deine Einkaufsliste ändern sich dadurch nie.",
        effectOther: "Gespeichert als Hinweis; ändert die Planerstellung noch nicht.",
        deleteButton: "Löschen",
        deleteConfirmTitle: "Diese Erinnerung löschen?",
        deleteConfirmBody: "Amy berücksichtigt das bei deiner Essensplanung nicht mehr und spricht es nicht wieder an, außer du sagst es ihr erneut.",
        deleteConfirmButton: "Löschen",
        cancelButton: "Abbrechen",
        deleteError: "Das konnte nicht gelöscht werden. Bitte versuche es erneut.",
    },
};
